import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';
import { analyticsService } from 'src/services/analytics/analyticsService';
import { RFQ_BUILDER_INTRO_CONTINUED } from 'src/services/analytics/rfqBuilderEvents';
import {
  ExitTypingText,
  MapComponent,
  TypingText
} from 'src/shared/components';
import { useMediaQuery } from 'src/shared/hooks';
import { Rfq } from 'src/shared/types/rfq';

import { ButtonV2, Typography } from 'src/shared/components/ui';
import { isWebGLAvailable } from 'src/shared/components/WebGLErrorBoundary';

import useSuppliersNumber from '../hooks/useSuppliersNumber';
import BlobFallback from './BlobFallback';
import IntroSupplierLoader from './IntroSupplierLoader';

interface RfqBuilderIntroProps {
  handleNext: () => void;
  rfq?: Rfq;
}

const RfqBuilderIntro: React.FC<RfqBuilderIntroProps> = ({
  handleNext,
  rfq
}) => {
  const [, setSearchParams] = useSearchParams();
  const [firstAndSecondComplete, setFirstAndSecondComplete] = useState(false);
  const [typingFinished, setTypingFinished] = useState(false);
  const [lookingForSuppliersComplete, setLookingForSuppliersComplete] =
    useState(false);
  const [isLookingForSuppliersExiting, setIsLookingForSuppliersExiting] =
    useState(false);
  const [showNiceText, setShowNiceText] = useState(false);
  const [thirdComplete, setThirdComplete] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMobile = useMediaQuery();

  // Use the same supplier estimation logic as the quotes tab
  const {
    fetchSuppliersNumber,
    numOfSuppliers: supplierCount = 0,
    isValidProduct
  } = useSuppliersNumber() || {
    fetchSuppliersNumber: () => {},
    numOfSuppliers: 0,
    isValidProduct: true
  };

  // Fetch supplier estimation when component mounts and RFQ name is available,
  // then persist the result in the RFQ document for use in contact estimation.
  useEffect(() => {
    if (rfq?.rfq?.document?.name) {
      fetchSuppliersNumber(rfq.rfq.document.name, rfq.rfq.id);
    }
  }, [rfq?.rfq?.document?.name]);

  const firstAndSecondText = `Finding manufacturers for: <strong>${rfq?.rfq?.document?.name}</strong>`;
  const lookingForSuppliersText = 'Looking for suppliers with a good fit';
  const thirdAndFourthText = isValidProduct
    ? `Nice - I've already found <strong>${supplierCount}</strong> suppliers that could be a great fit`
    : "This doesn't seem like a valid product";

  const firstDelay = 1200;
  const firstAndSecondTypingDuration =
    firstAndSecondText.replace(/<[^>]*>/g, '').length * 30;
  const lookingForSuppliersDelay =
    firstDelay + firstAndSecondTypingDuration + 600;

  // Start exit animation 4 seconds after "Looking for suppliers" completes
  useEffect(() => {
    if (lookingForSuppliersComplete && !isLookingForSuppliersExiting) {
      const timer = setTimeout(() => {
        setIsLookingForSuppliersExiting(true);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [lookingForSuppliersComplete, isLookingForSuppliersExiting]);

  // Handle exit animation complete - show "Nice" text
  const handleLookingForSuppliersExitComplete = () => {
    setShowNiceText(true);
  };

  // Delay before transitioning to ExitTypingText after typing finishes
  useEffect(() => {
    if (typingFinished) {
      const timer = setTimeout(() => {
        setLookingForSuppliersComplete(true);
      }, 1250);

      return () => clearTimeout(timer);
    }
  }, [typingFinished]);

  useEffect(() => {
    if (firstAndSecondComplete && thirdComplete) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setShowMap(true);
      }, 600);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    } else {
      setShowMap(false);
    }
  }, [firstAndSecondComplete, thirdComplete]);

  if (!rfq?.rfq) {
    return null;
  }

  return (
    <>
      <div className="relative h-dvh w-full overflow-hidden">
        <AnimatePresence>
          {firstAndSecondComplete && !showNiceText && (
            <motion.div
              key="intro-supplier-loader"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.3 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2, ease: 'easeInOut' }}
              className="fixed inset-0 z-0"
            >
              {isWebGLAvailable() ? (
                <IntroSupplierLoader
                  key="supplier-loader-instance"
                  className="w-full h-full"
                  initialCameraPosition={
                    isMobile
                      ? { x: -1000, y: -1500, z: -3000 }
                      : { x: 800, y: -800, z: -1800 }
                  }
                />
              ) : (
                <BlobFallback isMobile={isMobile ?? false} variant="intro" />
              )}
            </motion.div>
          )}
        </AnimatePresence>
        <div className="h-full flex flex-col mobile:px-6 px-[10%] mt-[15%] mobile:mt-[30%] items-start gap-6 relative z-30">
          <Typography
            as="h1"
            variant="h1"
            weight="bold"
            className="mobile:text-[32px] text-[40px]"
          >
            Perfect! Let&apos;s dive in
          </Typography>
          <div
            className="flex flex-col gap-4 mobile:w-full w-[50%]"
            translate="no"
          >
            <Typography
              as="p"
              variant="body-1"
              className="mobile:text-[20px] text-[28px]"
            >
              <TypingText
                fullText={firstAndSecondText}
                delay={firstDelay}
                speed={30}
                onComplete={() => setFirstAndSecondComplete(true)}
              />
            </Typography>
            {!showNiceText && (
              <Typography
                as="p"
                variant="body-1"
                className="mobile:text-[20px] text-[28px]"
              >
                {!lookingForSuppliersComplete ? (
                  <TypingText
                    fullText={lookingForSuppliersText}
                    delay={lookingForSuppliersDelay}
                    speed={25}
                    onComplete={() => setTypingFinished(true)}
                  />
                ) : (
                  <ExitTypingText
                    fullText={lookingForSuppliersText}
                    isExiting={isLookingForSuppliersExiting}
                    speed={10}
                    onExitComplete={handleLookingForSuppliersExitComplete}
                  />
                )}
              </Typography>
            )}
            {showNiceText && (
              <Typography
                as="p"
                variant="body-1"
                className="mobile:text-[20px] text-[28px]"
              >
                <TypingText
                  fullText={thirdAndFourthText}
                  delay={0}
                  speed={30}
                  onComplete={() => setThirdComplete(true)}
                />
              </Typography>
            )}
          </div>
          {showMap && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, ease: 'easeInOut', delay: 1 }}
              className="flex gap-4"
            >
              <ButtonV2
                onPress={() => {
                  analyticsService.trackEvent(RFQ_BUILDER_INTRO_CONTINUED, {
                    rfq_id: rfq?.rfq?.id,
                    supplier_count: supplierCount
                  });
                  setSearchParams({
                    rfq: rfq?.rfq?.id.toString(),
                    step: 'path'
                  });
                  handleNext();
                }}
                size="lg"
                endContent={<ArrowRightIcon className="size-4" />}
                className="h-14 rounded-full px-6 backdrop-blur-2xl opacity-90"
              >
                Continue
              </ButtonV2>
            </motion.div>
          )}
        </div>

        {thirdComplete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, ease: 'easeInOut' }}
            className="h-dvh mobile:w-[80%] w-[60%] overflow-hidden flex items-center justify-center fixed right-0 mobile:left-0 mobile:mx-auto mobile:right-0 top-0 mobile:top-[20%] z-10"
          >
            <MapComponent key="map-component" />
          </motion.div>
        )}
      </div>
    </>
  );
};

export default RfqBuilderIntro;
